import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';

export type AppVersion = '1' | '2';

const STORAGE_KEY = 'relight-studio-version';

interface VersionState {
  version: AppVersion;
  isInitialized: boolean;
}

export interface VersionContextType extends VersionState {
  setVersion: (version: AppVersion) => void;
}

const initialState: VersionState = {
  version: '1',
  isInitialized: false,
};

export const VersionContext = createContext<VersionContextType>({
  ...initialState,
  setVersion: () => {},
});

interface VersionProviderProps {
  children?: ReactNode;
}

export const VersionProvider: FC<VersionProviderProps> = ({ children }) => {
  const [state, setState] = useState<VersionState>(initialState);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      setState({
        version: (stored === '1' || stored === '2') ? stored : '1',
        isInitialized: true,
      });
    } catch {
      setState((prev) => ({ ...prev, isInitialized: true }));
    }
  }, []);

  const setVersion = useCallback((version: AppVersion): void => {
    try {
      window.localStorage.setItem(STORAGE_KEY, version);
    } catch {}
    setState((prev) => ({ ...prev, version }));
  }, []);

  return (
    <VersionContext.Provider value={{ ...state, setVersion }}>
      {children}
    </VersionContext.Provider>
  );
};

export const VersionConsumer = VersionContext.Consumer;
